import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Stack,
  Tooltip,
  IconButton,
  Collapse,
  Button,
  Autocomplete,
  TablePagination,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  styled
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { tr as trLocale, enUS } from 'date-fns/locale';
import { format, isWithinInterval, parseISO, addDays, subDays } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ClearIcon from '@mui/icons-material/Clear';
import DateRangeIcon from '@mui/icons-material/DateRange';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import { Navigation } from '@mui/icons-material'; // Navigasyon ikonunu import et
import { useAuth } from '../../contexts/AuthContext';
import firebaseDataService from '../../services/FirebaseDataService';
import * as shiftDataService from '../../services/migration/shiftDataService';
import VardiyaZamanDuzenleModal from './VardiyaZamanDuzenleModal';
import CihazBilgileriModal from './CihazBilgileriModal';
import ShiftLocationModal from './modals/ShiftLocationModal'; // Yeni modalı import et
import { useTranslation } from 'react-i18next';

interface GirisCikisRaporlariProps {
  branchId?: string;
  isManager?: boolean;
}

// Durum seçeneği tipi tanımı
interface StatusOption {
  value: string;
  label: string;
  color: string;
}

// Mobil rapor kartı için styled component
const ReportCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
  },
}));

const GirisCikisRaporlari: React.FC<GirisCikisRaporlariProps> = ({ branchId, isManager = false }) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const { currentUser, userDetails } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState<any[]>([]);
  const [filteredReports, setFilteredReports] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [personnelData, setPersonnelData] = useState<{[key: string]: any}>({});
  
  // Şube state değişkenleri
  const [branches, setBranches] = useState<any[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string>('all');
  
  // Sayfalandırma için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Tarih filtresi için state
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  
  // Datepicker açma fonksiyonları için state
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);
  
  // Durum listesi
  const statusOptions: StatusOption[] = [
    { value: 'all', label: tr('Tüm Durumlar', 'All Statuses'), color: 'default' },
    { value: 'devam-eden-personel', label: tr('Devam Eden Personel', 'Ongoing Personnel'), color: 'info' },
    { value: 'unutulanlar', label: tr('Unutulanlar', 'Missed Records'), color: 'error' },
    { value: 'elle-duzenlenenler', label: tr('Elle Düzenlenenler', 'Manually Edited'), color: 'warning' },
    { value: 'Normal Giriş', label: tr('Normal Giriş', 'Normal Check-In'), color: 'success' },
    { value: 'Erken Geldi', label: tr('Erken Geldi', 'Arrived Early'), color: 'info' },
    { value: 'Geç Geldi', label: tr('Geç Geldi', 'Arrived Late'), color: 'warning' },
    { value: 'Normal Çıktı', label: tr('Normal Çıktı', 'Normal Check-Out'), color: 'success' },
    { value: 'Vardiya Tamamlandı', label: tr('Vardiya Tamamlandı', 'Shift Completed'), color: 'success' },
    { value: 'Erken Çıktı', label: tr('Erken Çıktı', 'Left Early'), color: 'warning' },
    { value: 'Devam Ediyor', label: tr('Devam Ediyor', 'Ongoing'), color: 'info' },
    { value: 'Giriş Yapılmamış', label: tr('Giriş Yapılmamış', 'No Check-In'), color: 'error' },
    { value: 'Çıkış Yapılmamış', label: tr('Çıkış Yapılmamış', 'No Check-Out'), color: 'error' }
  ];
  
  // Çalışma süresi hesaplama fonksiyonu
  const calculateWorkingHours = (checkInTime: string, checkOutTime: string): string => {
    // Eğer giriş veya çıkış yapılmamışsa hesaplanmaz
    if (checkInTime === '--:--' || checkOutTime === '--:--') {
      return '--:--';
    }
    
    try {
      // Saat ve dakikaları parse et
      const [inHour, inMinute] = checkInTime.split(':').map(Number);
      const [outHour, outMinute] = checkOutTime.split(':').map(Number);
      
      // Dakika cinsinden toplam süre hesapla
      let totalMinutes = (outHour * 60 + outMinute) - (inHour * 60 + inMinute);
      
      // Çıkış saati giriş saatinden önceyse (gün değişimi olmuş)
      if (totalMinutes < 0) {
        totalMinutes += 24 * 60; // 24 saat ekle
      }
      
      // Saat ve dakika olarak dönüştür
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      
      // Formatla
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } catch (error) {
      console.error('Çalışma süresi hesaplama hatası:', error);
      return '--:--';
    }
  };
  
  // Dakika cinsinden çalışma süresini hesaplama
  const calculateWorkingMinutes = (checkInTime: string, checkOutTime: string): number => {
    // Eğer giriş veya çıkış yapılmamışsa hesaplanmaz
    if (checkInTime === '--:--' || checkOutTime === '--:--') {
      return 0;
    }
    
    try {
      // Saat ve dakikaları parse et
      const [inHour, inMinute] = checkInTime.split(':').map(Number);
      const [outHour, outMinute] = checkOutTime.split(':').map(Number);
      
      // Dakika cinsinden toplam süre hesapla
      let totalMinutes = (outHour * 60 + outMinute) - (inHour * 60 + inMinute);
      
      // Çıkış saati giriş saatinden önceyse (gün değişimi olmuş)
      if (totalMinutes < 0) {
        totalMinutes += 24 * 60; // 24 saat ekle
      }
      
      return totalMinutes;
    } catch (error) {
      console.error('Çalışma dakikası hesaplama hatası:', error);
      return 0;
    }
  };
  
  // Dakikayı saat:dakika formatına çevirme
  const formatMinutesToHours = (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };
  
  // Bir rapor için efektif çalışma dakikası (dual → sunucu workedMinutes; single → saat farkı).
  const reportWorkedMinutes = (report: any): number => {
    if (report?.attendanceMode === 'dual') {
      return typeof report.workedMinutesServer === 'number' ? report.workedMinutesServer : 0;
    }
    return calculateWorkingMinutes(report.checkIn, report.checkOut);
  };

  // Bir rapor için toplam süre etiketi (dual anomalilerde '--:--').
  const reportTotalHours = (report: any): string => {
    if (report?.attendanceMode === 'dual') {
      if (typeof report.workedMinutesServer === 'number' && report.workedMinutesServer > 0) {
        return formatMinutesToHours(report.workedMinutesServer);
      }
      return '--:--';
    }
    return calculateWorkingHours(report.checkIn, report.checkOut);
  };

  // Seçili durum
  const selectedStatus = statusOptions.find(option => option.value === statusFilter) || statusOptions[0];

  const localizeStatus = (status: string) => {
    switch (status) {
      case 'Normal Giriş': return tr('Normal Giriş', 'Normal Check-In');
      case 'Normal Geldi': return tr('Normal Geldi', 'Arrived On Time');
      case 'Erken Geldi': return tr('Erken Geldi', 'Arrived Early');
      case 'Geç Geldi': return tr('Geç Geldi', 'Arrived Late');
      case 'Giriş Yapılmamış': return tr('Giriş Yapılmamış', 'No Check-In');
      case 'Normal Çıktı': return tr('Normal Çıktı', 'Normal Check-Out');
      case 'Vardiya Tamamlandı': return tr('Vardiya Tamamlandı', 'Shift Completed');
      case 'Erken Çıktı': return tr('Erken Çıktı', 'Left Early');
      case 'Devam Ediyor': return tr('Devam Ediyor', 'Ongoing');
      case 'Çıkış Yapılmamış': return tr('Çıkış Yapılmamış', 'No Check-Out');
      case 'Giriş Durumu Belirsiz': return tr('Giriş Durumu Belirsiz', 'Check-In Status Unknown');
      case 'Çıkış Durumu Belirsiz': return tr('Çıkış Durumu Belirsiz', 'Check-Out Status Unknown');
      default: return status;
    }
  };
  
  // Vardiya zaman düzenleme modalı için state
  const [selectedReport, setSelectedReport] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Cihaz bilgileri modalı için state
  const [selectedDeviceInfo, setSelectedDeviceInfo] = useState<any | null>(null);
  const [deviceActionType, setDeviceActionType] = useState<'giris' | 'cikis'>('giris');
  const [isCihazModalOpen, setIsCihazModalOpen] = useState(false);
  
  // Konum modalı için state'ler
  const [shiftLocationModalOpen, setShiftLocationModalOpen] = useState(false);
  const [selectedReportForLocation, setSelectedReportForLocation] = useState<any | null>(null);

  // Filtre görünürlüğü için state - Varsayılan kapalı
  const [showFilters, setShowFilters] = useState(false);

  // Takvim referansları
  const startDatePickerRef = React.useRef<any>(null);
  const endDatePickerRef = React.useRef<any>(null);

  // Hızlı tarih seçme
  const setQuickDateRange = (range: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch(range) {
      case 'today':
        setStartDate(today);
        setEndDate(today);
        break;
      case 'yesterday':
        const yesterday = subDays(today, 1);
        setStartDate(yesterday);
        setEndDate(yesterday);
        break;
      case 'thisWeek':
        const thisWeekStart = new Date(today);
        thisWeekStart.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1)); // Pazartesi
        setStartDate(thisWeekStart);
        setEndDate(today);
        break;
      case 'lastWeek':
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - today.getDay() - 6); // Geçen haftanın Pazartesi
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay()); // Geçen haftanın Pazar
        setStartDate(lastWeekStart);
        setEndDate(lastWeekEnd);
        break;
      case 'thisMonth':
        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        setStartDate(thisMonthStart);
        setEndDate(today);
        break;
      case 'lastMonth':
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        setStartDate(lastMonthStart);
        setEndDate(lastMonthEnd);
        break;
      case 'last30Days':
        setStartDate(subDays(today, 30));
        setEndDate(today);
        break;
      default:
        break;
    }
  };
  
  // Tarih filtresini sıfırla
  const resetDateFilter = () => {
    setStartDate(null);
    setEndDate(null);
  };

  // Modal işlemleri
  const handleOpenModal = (report: any) => {
    setSelectedReport(report);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReport(null);
  };
  
  // Cihaz bilgileri modalı işlemleri
  const handleOpenCihazModal = (deviceInfo: any, actionType: 'giris' | 'cikis', e: React.MouseEvent) => {
    e.stopPropagation(); // Modal açarken satır tıklamasını engelle
    setSelectedDeviceInfo(deviceInfo);
    setDeviceActionType(actionType);
    setIsCihazModalOpen(true);
  };

  const handleCloseCihazModal = () => {
    setIsCihazModalOpen(false);
    setSelectedDeviceInfo(null);
  };

  // Konum modalı işlemleri
  const handleOpenShiftLocationModal = (report: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedReportForLocation(report);
    setShiftLocationModalOpen(true);
  };

  const handleCloseShiftLocationModal = () => {
    setShiftLocationModalOpen(false);
    setSelectedReportForLocation(null);
  };

  // Cihaz modelini göstermek için fonksiyonu güncelle
  const getDeviceModel = (deviceInfo: any) => {
    if (!deviceInfo) return 'Bilinmiyor';
    
    if (deviceInfo.androidInfo) {
      return `${deviceInfo.androidInfo.brand || ''} ${deviceInfo.androidInfo.model || ''}`.trim() || 'Android Cihaz';
    }
    
    if (deviceInfo.iosInfo) {
      return deviceInfo.iosInfo.model || 'iPhone';
    }
    
    if (deviceInfo.webInfo) {
      return 'Web Tarayıcısı';
    }
    
    // Eski format cihaz bilgisi (doğrudan brand ve model alanları olanlar)
    if (deviceInfo.brand || deviceInfo.model) {
      return `${deviceInfo.brand || ''} ${deviceInfo.model || ''}`.trim() || 'Bilinmiyor';
    }
    
    return 'Bilinmiyor';
  };

  // Veritabanından vardiya giriş/çıkış raporlarını real-time olarak çek
  useEffect(() => {
    if (!userDetails?.companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Şube verilerini çek (Cloud Functions)
    const loadBranches = async () => {
      try {
        if (!userDetails.companyId) return;
        const branchesData = await firebaseDataService.getBranches(userDetails.companyId);
        if (branchesData) {
          const branchesArray = Object.entries(branchesData).map(([id, data]: [string, any]) => {
            // Şube yapısına göre adını kontrol et
            let branchName = 'İsimsiz Şube';
            if (data.name) {
              branchName = data.name;
            } else if (data.basicInfo && data.basicInfo.name) {
              branchName = data.basicInfo.name;
            }
            
            return {
              id,
              name: branchName
            };
          });
          
          setBranches(branchesArray);
          
          // Şube yöneticisi ise otomatik kendi şubesini seç
          if (isManager && branchId) {
            setSelectedBranch(branchId);
          }
        } else {
          setBranches([]);
        }
      } catch (error) {
        console.error("Şube verileri yüklenirken hata:", error);
      }
    };
    loadBranches();
    
    // Personel (şube filtresi) + vardiyalar (isNextDayExit) + attendance (SQL: gts_hr_api)
    const loadData = async () => {
      try {
        if (!userDetails.companyId) return;

        // Tarih penceresi: son 30 gün (yyyy-MM-dd)
        const toDate = format(new Date(), 'yyyy-MM-dd');
        const fromDate = format(subDays(new Date(), 30), 'yyyy-MM-dd');

        const [personnelResult, shiftsRaw] = await Promise.all([
          firebaseDataService.getPersonnel(userDetails.companyId),
          shiftDataService.getShifts(userDetails.companyId),
        ]);

        const personnelObj: {[key: string]: any} = {};
        if (personnelResult) {
          if (Array.isArray(personnelResult)) {
            personnelResult.forEach((p: any) => { personnelObj[p.id] = p; });
          } else {
            Object.keys(personnelResult as any).forEach(id => {
              personnelObj[id] = (personnelResult as any)[id];
            });
          }
        }
        setPersonnelData(personnelObj);

        // ShiftId → isNextDayExit haritası
        const nextDayMap: Record<string, boolean> = {};
        shiftsRaw.forEach((s) => { nextDayMap[String(s.id)] = !!s.isNextDayExit; });

        // Attendance kayıtlarını sayfalı çek (tüm sayfalar — client-side filtre/sayfalama korunur)
        const all: any[] = [];
        let pageNo = 1;
        const pageSize = 200;
        for (let i = 0; i < 100; i++) {
          const res = await shiftDataService.getAttendanceList({ from: fromDate, to: toDate, page: pageNo, pageSize });
          all.push(...res.items);
          if (all.length >= res.total || res.items.length === 0) break;
          pageNo++;
        }

        const parseJson = (s: any) => {
          if (!s) return null;
          if (typeof s === 'object') return s;
          try { return JSON.parse(s); } catch { return null; }
        };
        const toMs = (iso: any) => (iso ? new Date(iso).getTime() : 0);
        const toRtdbDate = (ymd: string) => {
          const [y, m, d] = ymd.split('-');
          return `${d}-${m}-${y}`;
        };
        const formatTime = (timestamp: number) => {
          if (!timestamp) return '--:--';
          const d = new Date(timestamp);
          return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
        };

        const reportsList: any[] = [];
        for (const e of all) {
          const personelId = e.personnelUid;
          const personnel = personnelObj[personelId] || { name: '' };

          if (isManager && branchId) {
            const personBranchId = personnel.branchesId || personnel.branchId || personnel.branch;
            let isPersonInBranch = false;
            if (typeof personBranchId === 'string') {
              isPersonInBranch = personBranchId === branchId;
            } else if (personBranchId && typeof personBranchId === 'object') {
              if ('id' in personBranchId && personBranchId.id === branchId) isPersonInBranch = true;
              else if (Object.keys(personBranchId).includes(branchId)) isPersonInBranch = true;
            }
            if (!isPersonInBranch) continue;
          }

          const girisMs = toMs(e.checkInTime);
          const cikisMs = toMs(e.checkOutTime);

          const statusInfo = { entryStatus: '', exitStatus: '' };
          if (e.checkInStatus) {
            const s = String(e.checkInStatus).toLowerCase();
            if (s === 'early') statusInfo.entryStatus = 'Erken Geldi';
            else if (s === 'late') statusInfo.entryStatus = 'Geç Geldi';
            else if (s === 'giriş yapılmamış') statusInfo.entryStatus = 'Giriş Yapılmamış';
            else if (s === 'normal') statusInfo.entryStatus = 'Normal Geldi';
            else if (['ontime', 'on-time', 'on_time'].includes(s)) statusInfo.entryStatus = 'Normal Giriş';
            else statusInfo.entryStatus = e.checkInStatus;
          } else {
            statusInfo.entryStatus = 'Giriş Durumu Belirsiz';
          }

          if (cikisMs) {
            if (e.checkOutStatus) {
              const s = String(e.checkOutStatus).toLowerCase();
              if (['earlyexit', 'early-exit', 'early_exit'].includes(s)) statusInfo.exitStatus = 'Erken Çıktı';
              else if (s === 'çıkış yapılmamış') statusInfo.exitStatus = 'Çıkış Yapılmamış';
              else if (s === 'normal') statusInfo.exitStatus = 'Normal Çıktı';
              else if (['complete', 'completed'].includes(s)) statusInfo.exitStatus = 'Vardiya Tamamlandı';
              else statusInfo.exitStatus = e.checkOutStatus;
            } else {
              statusInfo.exitStatus = 'Çıkış Durumu Belirsiz';
            }
          } else {
            statusInfo.exitStatus = 'Devam Ediyor';
          }

          const rtdbDate = toRtdbDate(e.attendanceDate);
          // RTDB record şekline eşle (alt bileşenler ve filtreler bu yapıyı bekler)
          const originalRecord = {
            girisZamani: girisMs,
            cikisZamani: cikisMs || null,
            girisDurumu: e.checkInStatus,
            cikisDurumu: e.checkOutStatus,
            girisKonum: parseJson(e.checkInLocation),
            cikisKonum: parseJson(e.checkOutLocation),
            girisDeviceInfo: parseJson(e.checkInDevice),
            cikisDeviceInfo: parseJson(e.checkOutDevice),
            // Dual: her yön ayrı punch → düzeltme izi yön bazında (checkInEdited/checkOutEdited).
            girisElleDuzenlendi: e.checkInEdited != null ? !!e.checkInEdited : !!e.isManuallyEdited,
            cikisElleDuzenlendi: e.checkOutEdited != null ? !!e.checkOutEdited : !!e.isManuallyEdited,
            // Yönetici saati değiştirdiyse personelin gerçekte okuttuğu an / elle eklendi mi.
            girisOkutulan: e.checkInScannedTime ? new Date(e.checkInScannedTime).getTime() : null,
            cikisOkutulan: e.checkOutScannedTime ? new Date(e.checkOutScannedTime).getTime() : null,
            girisElle: !!e.checkInManual,
            cikisElle: !!e.checkOutManual,
            vardiyaAdi: e.shiftName,
            vardiyaId: e.shiftId,
          };

          const isDual = e.attendanceMode === 'dual';
          reportsList.push({
            // Dual günde çoklu oturum → id sessionIndex ile benzersiz (React key + satır kimliği).
            id: `${rtdbDate}_${personelId}_${isDual ? (e.sessionIndex ?? 0) : 'x'}`,
            personnel: personnel.name || personelId,
            personnelId: personelId,
            shift: e.shiftName || 'Bilinmeyen Vardiya',
            date: rtdbDate,
            attendanceDateIso: e.attendanceDate, // edit modalı için (yyyy-MM-dd)
            checkIn: formatTime(girisMs),
            checkOut: cikisMs ? formatTime(cikisMs) : '--:--',
            statusInfo,
            originalRecord,
            isNextDayExit: e.shiftId != null ? !!nextDayMap[String(e.shiftId)] : false,
            // Dual (çift-QR) oturum alanları
            attendanceMode: isDual ? 'dual' : 'single',
            anomaly: e.anomaly || null, // null | missing_out | orphan_out
            isOpen: !!e.isOpen,
            punchInId: e.punchInId ?? null,
            punchOutId: e.punchOutId ?? null,
            sessionIndex: e.sessionIndex ?? 0,
            workedMinutesServer: typeof e.workedMinutes === 'number' ? e.workedMinutes : null,
            shiftId: e.shiftId ?? null,
            // Aynı gün+personelde çoklu oturum etiketi (aşağıda doldurulur)
            sessionNo: 0,
            sessionTotal: 0,
          });
        }

        // Dual: aynı (personel, gün) içindeki oturumları sırala ve "Oturum n/N" etiketle.
        const dualGroups: Record<string, any[]> = {};
        for (const r of reportsList) {
          if (r.attendanceMode !== 'dual') continue;
          const key = `${r.personnelId}_${r.date}`;
          (dualGroups[key] ||= []).push(r);
        }
        Object.values(dualGroups).forEach((group) => {
          if (group.length < 2) return;
          group.sort((a, b) => {
            const at = a.originalRecord.girisZamani || a.originalRecord.cikisZamani || 0;
            const bt = b.originalRecord.girisZamani || b.originalRecord.cikisZamani || 0;
            return at - bt;
          });
          group.forEach((r, idx) => {
            r.sessionNo = idx + 1;
            r.sessionTotal = group.length;
          });
        });

        reportsList.sort((a, b) => {
          const aDateParts = a.date.split('-');
          const bDateParts = b.date.split('-');
          const aDate = new Date(parseInt(aDateParts[2], 10), parseInt(aDateParts[1], 10) - 1, parseInt(aDateParts[0], 10));
          const bDate = new Date(parseInt(bDateParts[2], 10), parseInt(bDateParts[1], 10) - 1, parseInt(bDateParts[0], 10));
          return bDate.getTime() - aDate.getTime();
        });

        setReports(reportsList);
      } catch (error) {
        console.error("Vardiya raporları yüklenirken hata:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [userDetails, isManager, branchId]);

  // Filtreleme işlemi
  useEffect(() => {
    let filtered = [...reports];
    
    // Debug: Filtreleme öncesi raporları kontrol et
    if (reports.length > 0) {
      console.log('Toplam rapor sayısı:', reports.length);
      console.log('İlk rapor örneği:', reports[0]);
      // Cihaz bilgisi kontrolü
      reports.forEach((report, index) => {
        if (report.originalRecord?.girisDeviceInfo) {
          console.log(`Rapor ${index} - ${report.date} - ${report.personnel} giriş cihaz bilgisi var`);
        }
        if (report.originalRecord?.cikisDeviceInfo) {
          console.log(`Rapor ${index} - ${report.date} - ${report.personnel} çıkış cihaz bilgisi var`);
        }
      });
    }
    
    // Personel veya vardiya araması
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(report => 
        report.personnel.toLowerCase().includes(term) || 
        report.shift.toLowerCase().includes(term)
      );
    }
    
    // Durum filtresi
    if (statusFilter && statusFilter !== 'all') {
      if (statusFilter === 'devam-eden-personel') {
        // Giriş yapmış fakat henüz çıkış yapmamış personelleri filtrele
        filtered = filtered.filter(report => 
          report.checkIn !== '--:--' && report.checkOut === '--:--' && report.statusInfo.exitStatus === 'Devam Ediyor'
        );
      } else if (statusFilter === 'unutulanlar') {
        // Bugünün tarihini al
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Geçmiş günlerde giriş veya çıkış yapılmamış kayıtları filtrele
        filtered = filtered.filter(report => {
          // Rapor tarihini Date nesnesine çevir
          const dateParts = report.date.split('-');
          const day = parseInt(dateParts[0], 10);
          const month = parseInt(dateParts[1], 10) - 1; // JavaScript ayları 0'dan başlar
          const year = parseInt(dateParts[2], 10);
          const reportDate = new Date(year, month, day);
          
          // Rapor bugünden önceki bir güne ait ve eksik giriş/çıkış var mı?
          return reportDate < today && (report.checkIn === '--:--' || report.checkOut === '--:--');
        });
      } else if (statusFilter === 'elle-duzenlenenler') {
        // Elle düzenlenmiş kayıtları filtrele
        filtered = filtered.filter(report => 
          report.originalRecord.girisElleDuzenlendi || report.originalRecord.cikisElleDuzenlendi
        );
      } else {
        // Diğer durum filtreleri
        filtered = filtered.filter(report => {
          const { entryStatus, exitStatus } = report.statusInfo;
          return entryStatus === statusFilter || exitStatus === statusFilter;
        });
      }
    }
    
    // Şube filtresi
    if (selectedBranch !== 'all') {
      if (selectedBranch === 'unassigned') {
        // Şubesi atanmamış personelleri filtrele
        filtered = filtered.filter(report => {
          // Personel bilgisini bul
          const person = personnelData[report.personnelId];
          if (!person) return false;
          
          // Personelin şube bilgisinin olmamasını kontrol et
          return !person.branchesId && !person.branchId && !person.branch;
        });
      } else {
        filtered = filtered.filter(report => {
          // Personel bilgisini bul
          const person = personnelData[report.personnelId];
          if (!person) return false;
          
          // Personelin şube bilgisini kontrol et
          const personBranchId = person.branchesId || person.branchId || person.branch;
          
          if (!personBranchId) {
            return false;
          }
          
          if (typeof personBranchId === 'string') {
            return personBranchId === selectedBranch;
          } else if (typeof personBranchId === 'object') {
            if ('id' in personBranchId && personBranchId.id === selectedBranch) {
              return true;
            }
            return Object.keys(personBranchId).includes(selectedBranch);
          }
          
          return false;
        });
      }
    }
    
    // Tarih aralığı filtresi
    if (startDate && endDate) {
      filtered = filtered.filter(report => {
        // Tarih string'ini Date nesnesine çevir (dd-MM-yyyy)
        const dateParts = report.date.split('-');
        if (dateParts.length !== 3) return false;
        
        const day = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1; // JavaScript ayları 0'dan başlar
        const year = parseInt(dateParts[2], 10);
        
        const reportDate = new Date(year, month, day);
        
        // Tarih aralığı içinde mi kontrol et
        return isWithinInterval(reportDate, { start: startDate, end: endDate });
      });
    }
    
    setFilteredReports(filtered);
  }, [searchTerm, statusFilter, reports, startDate, endDate, selectedBranch, personnelData]);

  // Excel'e veri aktarma olayını dinle
  useEffect(() => {
    const handleExportToExcel = (event: CustomEvent) => {
      const { worksheet } = event.detail;
      
      // Filtrelenmiş rapor verilerini Excel'e ekle
      filteredReports.forEach(report => {
        // Durum bilgisini oluştur (dual/çift QR vardiyada durum yok → boş)
        let statusText = '';
        if (report.attendanceMode !== 'dual') {
          const { entryStatus, exitStatus } = report.statusInfo;
          if (entryStatus) statusText = localizeStatus(entryStatus);
          if (exitStatus) {
            const localizedExit = localizeStatus(exitStatus);
            statusText = statusText ? `${statusText} & ${localizedExit}` : localizedExit;
          }
        }
        
        // Çalışma süresini hesapla
        const totalWorkingHours = reportTotalHours(report);
        
        worksheet.addRow({
          personnel: report.personnel,
          shift: report.shift,
          date: report.date,
          checkIn: report.checkIn,
          checkOut: report.checkOut,
          totalHours: totalWorkingHours, // Toplam süreyi Excel'e ekle
          status: statusText
        });
      });
      
      // Boş satır ekle
      worksheet.addRow({});
      
      // Genel toplamı ekle
      const totalRow = worksheet.addRow({
        personnel: '',
        shift: '',
        date: '',
        checkIn: '',
        checkOut: tr('GENEL TOPLAM:', 'GRAND TOTAL:'),
        totalHours: calculateTotalWorkingTime(filteredReports),
        status: ''
      });
      
      // Toplam satırını biçimlendir
      totalRow.eachCell((cell: any, colNumber: number) => {
        if (colNumber >= 5) { // Sadece "GENEL TOPLAM:" ve değeri için stillendirme
          cell.font = { bold: true, size: 12 };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: '3F51B5' } // Primary renk (yaklaşık)
          };
          cell.font.color = { argb: 'FFFFFF' }; // Beyaz yazı
        }
      });
      
      // Hücre birleştirme (Genel Toplam yazısı için)
      worksheet.mergeCells(`E${totalRow.number}:E${totalRow.number}`);
    };
    
    // Event listener'ı ekle
    window.addEventListener('export-report-to-excel', handleExportToExcel as EventListener);
    
    // Cleanup
    return () => {
      window.removeEventListener('export-report-to-excel', handleExportToExcel as EventListener);
    };
  }, [filteredReports]);

  // Durum rengini belirle
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Normal Geldi':
      case 'Normal Çıktı':
      case 'Normal Giriş':
      case 'Vardiya Tamamlandı':
        return 'success';
      case 'Erken Geldi':
        return 'info';
      case 'Geç Geldi':
        return 'warning';
      case 'Erken Çıktı':
        return 'warning';
      case 'Devam Ediyor':
        return 'info';
      case 'Giriş Yapılmamış':
      case 'Çıkış Yapılmamış':
        return 'error';
      default:
        return 'default';
    }
  };

  // Dual anomali rozeti bilgisi (girişsiz çıkış / çıkış eksik)
  // Yönetici düzeltmesi izi: saati değiştirilen okutmanın gerçek anı ya da "elle eklendi".
  const editTrail = (scannedMs: number | null | undefined, manual: boolean | undefined) => {
    if (manual) {
      return (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2 }}>
          {tr('elle eklendi', 'added manually')}
        </Typography>
      );
    }
    if (!scannedMs) return null;
    const d = new Date(scannedMs);
    const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return (
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2 }}>
        {tr(`düzenlendi · okutulan ${hhmm}`, `edited · scanned ${hhmm}`)}
      </Typography>
    );
  };

  // Gece yarısını geçen oturum (çıkış girişten sonraki takvim gününde).
  const crossesMidnight = (report: any): boolean => {
    const g = report.originalRecord?.girisZamani;
    const c = report.originalRecord?.cikisZamani;
    if (!g || !c) return false;
    return new Date(g).toDateString() !== new Date(c).toDateString();
  };

  const getAnomalyInfo = (report: any): { label: string } | null => {
    if (report.attendanceMode !== 'dual') return null;
    if (report.anomaly === 'orphan_out') return { label: tr('Girişsiz çıkış', 'Check-out w/o check-in') };
    if (report.anomaly === 'missing_out') return { label: tr('Çıkış eksik', 'Missing check-out') };
    return null;
  };

  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sayfalandırılmış rapor verileri
  const paginatedReports = React.useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredReports.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredReports, page, rowsPerPage]);

  // Filtre değişikliklerinde sayfa numarasını sıfırla
  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter, startDate, endDate]);

  // Toplam çalışma süresini hesapla (dual → oturum sunucu süresi; single → saat farkı)
  const calculateTotalWorkingTime = (reports: any[]): string => {
    let totalMinutes = 0;
    
    reports.forEach(report => {
      totalMinutes += reportWorkedMinutes(report);
    });
    
    return formatMinutesToHours(totalMinutes);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2" gutterBottom sx={{ mb: 0 }}>
          {tr('Giriş Çıkış Raporları', 'Check-In / Check-Out Reports')}
        </Typography>
        
        {/* Filtre göster/gizle butonu */}
        <Tooltip title={showFilters ? tr('Filtreleri Gizle', 'Hide Filters') : tr('Filtreleri Göster', 'Show Filters')}>
          <IconButton 
            onClick={() => setShowFilters(!showFilters)}
            color="primary"
            size="small"
            sx={{ 
              bgcolor: showFilters ? 'primary.light' : 'transparent',
              color: showFilters ? 'white' : 'primary.main',
              '&:hover': { 
                bgcolor: showFilters ? 'primary.main' : 'rgba(0, 0, 0, 0.04)' 
              } 
            }}
          >
            <FilterListIcon fontSize="small" sx={{ mr: 0.5 }} />
            {showFilters ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
      
      {/* Filtreleme Alanı */}
      <Collapse in={showFilters} timeout="auto">
        <Paper 
          elevation={1} 
          sx={{ 
            p: 2, 
            mb: 2, 
            borderRadius: 1,
            borderLeft: '4px solid',
            borderColor: 'primary.main',
            bgcolor: 'background.default'
          }}
        >
          <Grid container spacing={2}>
            {/* Personel/Vardiya Arama */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder={tr('Personel veya Vardiya Ara...', 'Search Personnel or Shift...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {/* Durum Filtresi - Autocomplete */}
            <Grid item xs={12} md={3}>
              <Autocomplete
                value={selectedStatus}
                onChange={(_, newValue: StatusOption | null) => {
                  setStatusFilter(newValue ? newValue.value : 'all');
                }}
                options={statusOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label={tr('Durum Filtresi', 'Status Filter')}
                    size="small"
                    placeholder={tr('Durum seçin...', 'Select status...')}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip 
                        label=" " 
                        size="small" 
                        sx={{ 
                          minWidth: 20, 
                          backgroundColor: 
                            option.color === 'default' ? 'grey.400' :
                            option.color === 'success' ? 'success.light' : 
                            option.color === 'warning' ? 'warning.light' : 
                            option.color === 'error' ? 'error.light' : 
                            'info.light'
                        }} 
                      />
                      <Typography 
                        variant="body2"
                        sx={{
                          fontWeight: option.value === 'devam-eden-personel' || 
                                     option.value === 'unutulanlar' ||
                                     option.value === 'elle-duzenlenenler'
                                     ? 'bold' : 'normal',
                          color: option.value === 'devam-eden-personel' 
                                  ? 'info.main' 
                                  : option.value === 'unutulanlar'
                                    ? 'error.main'
                                    : option.value === 'elle-duzenlenenler'
                                      ? 'warning.main'
                                      : 'inherit'
                        }}
                      >
                        {option.label}
                      </Typography>
                    </Stack>
                  </Box>
                )}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                disableClearable
              />
            </Grid>
            
            {/* Şube Filtresi */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" disabled={isManager}>
                <InputLabel id="branch-filter-label">{tr('Şube Filtresi', 'Branch Filter')}</InputLabel>
                <Select
                  labelId="branch-filter-label"
                  id="branch-filter"
                  value={selectedBranch}
                  label={tr('Şube Filtresi', 'Branch Filter')}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                >
                  <MenuItem value="all">{tr('Tüm Şubeler', 'All Branches')}</MenuItem>
                  <MenuItem value="unassigned">{tr('Şubesi Atanmamış', 'Unassigned Branch')}</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Tarih Aralığı Filtresi */}
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={isEnglish ? enUS : trLocale}>
                <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <DatePicker 
                        label={tr('Başlangıç', 'Start')}
                        value={startDate}
                        onChange={(newValue) => setStartDate(newValue)}
                        format="dd/MM/yyyy"
                        open={startPickerOpen}
                        onOpen={() => setStartPickerOpen(true)}
                        onClose={() => setStartPickerOpen(false)}
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                            InputProps: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <IconButton 
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setStartPickerOpen(true);
                                    }}
                                  >
                                    <CalendarTodayIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                              endAdornment: startDate && (
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setStartDate(null);
                                  }}
                                  size="small"
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              )
                            }
                          }
                        }}
                      />
                    </Box>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <DatePicker 
                        label={tr('Bitiş', 'End')}
                        value={endDate}
                        onChange={(newValue) => setEndDate(newValue)}
                        format="dd/MM/yyyy"
                        open={endPickerOpen}
                        onOpen={() => setEndPickerOpen(true)}
                        onClose={() => setEndPickerOpen(false)}
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                            InputProps: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <IconButton 
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEndPickerOpen(true);
                                    }}
                                  >
                                    <CalendarTodayIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                              endAdornment: endDate && (
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEndDate(null);
                                  }}
                                  size="small"
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              )
                            }
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  
                  {/* Hızlı Tarih Seçimleri */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    <Tooltip title={tr('Bugün', 'Today')}>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
                        onClick={() => setQuickDateRange('today')}
                      >
                        {tr('Bugün', 'Today')}
                      </Button>
                    </Tooltip>
                    <Tooltip title={tr('Dün', 'Yesterday')}>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
                        onClick={() => setQuickDateRange('yesterday')}
                      >
                        {tr('Dün', 'Yesterday')}
                      </Button>
                    </Tooltip>
                    <Tooltip title={tr('Bu Hafta', 'This Week')}>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
                        onClick={() => setQuickDateRange('thisWeek')}
                      >
                        {tr('Bu Hafta', 'This Week')}
                      </Button>
                    </Tooltip>
                    <Tooltip title={tr('Son 30 Gün', 'Last 30 Days')}>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
                        onClick={() => setQuickDateRange('last30Days')}
                      >
                        {tr('Son 30', 'Last 30')}
                      </Button>
                    </Tooltip>
                    {startDate && endDate && (
                      <Tooltip title={tr('Tarihleri Temizle', 'Clear Dates')}>
                        <Button 
                          size="small" 
                          color="error"
                          variant="outlined" 
                          sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
                          onClick={resetDateFilter}
                        >
                          {tr('Temizle', 'Clear')}
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>
      
      {/* Rapor Tablosu */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredReports.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, bgcolor: '#f8f9fa', borderRadius: 2 }}>
          <Typography variant="body1" color="text.secondary">
            {searchTerm || statusFilter !== 'all' || (startDate && endDate) ? tr('Arama sonucu bulunamadı', 'No results found') : tr('Henüz rapor bulunmuyor', 'No reports yet')}
          </Typography>
        </Box>
      ) : isMobile ? (
        /* Mobil Kart Görünümü */
        <Box sx={{ pb: 2 }}>
          {paginatedReports.map((report) => {
            const dateParts = report.date.split('-');
            const day = parseInt(dateParts[0], 10);
            const month = parseInt(dateParts[1], 10) - 1;
            const year = parseInt(dateParts[2], 10);
            const reportDate = new Date(year, month, day);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const isOldMissingEntry = reportDate < today && 
              (report.checkIn === '--:--' || report.checkOut === '--:--');
            const totalWorkingHours = reportTotalHours(report);
            
            return (
              <ReportCard 
                key={report.id} 
                onClick={() => handleOpenModal(report)}
                sx={{
                  borderLeft: isOldMissingEntry ? '4px solid' : '1px solid',
                  borderLeftColor: isOldMissingEntry ? 'error.main' : 'divider',
                  backgroundColor: isOldMissingEntry ? 'rgba(229, 115, 115, 0.05)' : 'background.paper',
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {/* Üst Kısım - Personel ve Tarih */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Typography 
                      variant="subtitle1" 
                      fontWeight="bold"
                      color={isOldMissingEntry ? 'error.main' : 'text.primary'}
                      sx={{ fontSize: '0.95rem' }}
                    >
                      {report.personnel}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color={isOldMissingEntry ? 'error.main' : 'text.secondary'}
                      fontWeight={isOldMissingEntry ? 'bold' : 'normal'}
                    >
                      {report.date}
                    </Typography>
                  </Box>
                  
                  {/* Vardiya Adı + oturum/anomali rozetleri */}
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ fontSize: '0.8rem' }}
                    >
                      {report.shift}
                    </Typography>
                    {report.sessionTotal > 1 && (
                      <Chip
                        label={tr(`Oturum ${report.sessionNo}/${report.sessionTotal}`, `Session ${report.sessionNo}/${report.sessionTotal}`)}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '10px', '& .MuiChip-label': { px: 0.5 } }}
                      />
                    )}
                    {getAnomalyInfo(report) && (
                      <Chip
                        label={getAnomalyInfo(report)!.label}
                        size="small"
                        color="warning"
                        sx={{ height: 18, fontSize: '10px', '& .MuiChip-label': { px: 0.5 } }}
                      />
                    )}
                  </Box>
                  
                  {/* Giriş / Çıkış / Toplam */}
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 1, 
                          textAlign: 'center', 
                          borderRadius: 1,
                          borderColor: isOldMissingEntry && report.checkIn === '--:--' ? 'error.main' : 'divider'
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                          {tr('Giriş', 'In')}
                        </Typography>
                        <Typography 
                          variant="body1" 
                          fontWeight="bold" 
                          color={isOldMissingEntry && report.checkIn === '--:--' ? 'error.main' : 'success.main'}
                          sx={{ fontSize: '1rem' }}
                        >
                          {report.checkIn}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 1, 
                          textAlign: 'center', 
                          borderRadius: 1,
                          borderColor: isOldMissingEntry && report.checkOut === '--:--' ? 'error.main' : 'divider'
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                          {tr('Çıkış', 'Out')}
                        </Typography>
                        <Typography 
                          variant="body1" 
                          fontWeight="bold" 
                          color={isOldMissingEntry && report.checkOut === '--:--' ? 'error.main' : 'error.main'}
                          sx={{ fontSize: '1rem' }}
                        >
                          {report.checkOut}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 1, 
                          textAlign: 'center', 
                          borderRadius: 1,
                          bgcolor: totalWorkingHours !== '--:--' ? 'primary.50' : 'transparent'
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                          {tr('Toplam', 'Total')}
                        </Typography>
                        <Typography 
                          variant="body1" 
                          fontWeight="bold" 
                          color={totalWorkingHours !== '--:--' ? 'primary.main' : 'text.secondary'}
                          sx={{ fontSize: '1rem' }}
                        >
                          {totalWorkingHours}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </ReportCard>
            );
          })}
          
          {/* Mobil Sayfalama */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <TablePagination
              component="div"
              count={filteredReports.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage=""
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
              sx={{
                '.MuiTablePagination-selectLabel': { display: 'none' },
                '.MuiTablePagination-select': { display: 'none' },
                '.MuiTablePagination-selectIcon': { display: 'none' },
              }}
            />
          </Box>
        </Box>
      ) : (
        /* Desktop Tablo Görünümü */
        <Box>
          <Paper sx={{ 
            width: '100%', 
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
            borderRadius: 2,
            mb: 1
          }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="22%">{tr('Personel', 'Personnel')}</TableCell>
                  <TableCell width="18%">{tr('Vardiya', 'Shift')}</TableCell>
                  <TableCell width="12%">{tr('Tarih', 'Date')}</TableCell>
                  <TableCell width="10%">{tr('Giriş', 'In')}</TableCell>
                  <TableCell width="10%">{tr('Çıkış', 'Out')}</TableCell>
                  <TableCell width="10%">{tr('Toplam', 'Total')}</TableCell>
                  <TableCell width="18%">{tr('Durum', 'Status')}</TableCell>
                  <TableCell width="10%">{tr('Cihaz', 'Device')}</TableCell>
                  <TableCell width="5%">{tr('Konum', 'Location')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedReports.map((report) => {
                  const dateParts = report.date.split('-');
                  const day = parseInt(dateParts[0], 10);
                  const month = parseInt(dateParts[1], 10) - 1;
                  const year = parseInt(dateParts[2], 10);
                  const reportDate = new Date(year, month, day);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const isOldMissingEntry = reportDate < today && 
                    (report.checkIn === '--:--' || report.checkOut === '--:--');
                  const girisDeviceInfo = report.originalRecord?.girisDeviceInfo;
                  const cikisDeviceInfo = report.originalRecord?.cikisDeviceInfo;
                  const girisKonum = report.originalRecord?.girisKonum;
                  const cikisKonum = report.originalRecord?.cikisKonum;
                  const girisDeviceModel = girisDeviceInfo ? getDeviceModel(girisDeviceInfo) : '';
                  const cikisDeviceModel = cikisDeviceInfo ? getDeviceModel(cikisDeviceInfo) : '';
                  const totalWorkingHours = reportTotalHours(report);
                  
                  return (
                  <TableRow 
                    key={report.id} 
                    hover
                    onClick={() => handleOpenModal(report)}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: isOldMissingEntry ? 'rgba(229, 115, 115, 0.1)' : 'inherit',
                      '&:hover': { 
                        backgroundColor: isOldMissingEntry ? 'rgba(229, 115, 115, 0.2)' : 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          bgcolor: isOldMissingEntry ? 'error.main' : 'primary.main',
                          mr: 1.5
                        }}
                      >
                        <PersonIcon fontSize="small" />
                      </Avatar>
                      <Typography 
                        variant="body2" 
                        fontWeight={isOldMissingEntry ? 'bold' : 'medium'}
                        color={isOldMissingEntry ? 'error.main' : 'inherit'}
                      >
                        {report.personnel}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography 
                        variant="body2"
                        color={isOldMissingEntry ? 'error.main' : 'inherit'}
                      >
                        {report.shift}
                      </Typography>
                      {report.sessionTotal > 1 && (
                        <Chip
                          label={tr(`Oturum ${report.sessionNo}/${report.sessionTotal}`, `Session ${report.sessionNo}/${report.sessionTotal}`)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '10px', '& .MuiChip-label': { px: 0.5 } }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CalendarTodayIcon 
                        fontSize="small" 
                        sx={{ 
                          mr: 0.5, 
                          color: isOldMissingEntry ? 'error.main' : 'text.secondary' 
                        }} 
                      />
                      <Typography 
                        variant="body2"
                        color={isOldMissingEntry ? 'error.main' : 'inherit'}
                        fontWeight={isOldMissingEntry ? 'bold' : 'normal'}
                      >
                        {report.date}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2"
                      sx={{ 
                        color: isOldMissingEntry && report.checkIn === '--:--' 
                          ? 'error.main' 
                          : report.originalRecord.girisElleDuzenlendi 
                            ? 'error.main' 
                            : 'inherit',
                        fontWeight: (isOldMissingEntry && report.checkIn === '--:--') || 
                                   report.originalRecord.girisElleDuzenlendi 
                                   ? 'bold' 
                                   : 'normal'
                      }}
                    >
                      {report.checkIn}
                    </Typography>
                    {editTrail(report.originalRecord.girisOkutulan, report.originalRecord.girisElle)}
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2"
                      sx={{ 
                        color: isOldMissingEntry && report.checkOut === '--:--' 
                          ? 'error.main' 
                          : report.originalRecord.cikisElleDuzenlendi 
                            ? 'error.main' 
                            : 'inherit',
                        fontWeight: (isOldMissingEntry && report.checkOut === '--:--') || 
                                   report.originalRecord.cikisElleDuzenlendi 
                                   ? 'bold' 
                                   : 'normal'
                      }}
                    >
                      {report.checkOut}
                      {(report.isNextDayExit || crossesMidnight(report)) && report.checkOut !== '--:--' && (
                        <Chip 
                          label={tr('Ertesi Gün', 'Next Day')} 
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ 
                            ml: 0.5, 
                            height: 16, 
                            fontSize: '9px',
                            '& .MuiChip-label': { px: 0.5 }
                          }}
                        />
                      )}
                    </Typography>
                    {editTrail(report.originalRecord.cikisOkutulan, report.originalRecord.cikisElle)}
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontWeight: 'bold',
                        color: totalWorkingHours !== '--:--' ? 'success.main' : 'text.secondary'
                      }}
                    >
                      {totalWorkingHours}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {getAnomalyInfo(report) && (
                        <Chip
                          label={getAnomalyInfo(report)!.label}
                          size="small"
                          color="warning"
                          sx={{ fontSize: '11px', height: 24, mb: 0.5 }}
                        />
                      )}
                      {/* Dual (çift QR): erken/geç/erken çıkış durumu GÖSTERİLMEZ (çoklu giriş-çıkış desteklenir; etiket yanıltıcı olur). Sadece anomali rozeti kalır.
                          Single: orphan_out → giriş durumu gizli; missing_out → çıkış durumu gizli. */}
                      {report.attendanceMode !== 'dual' && report.statusInfo.entryStatus && report.anomaly !== 'orphan_out' && (
                        <Chip 
                          label={localizeStatus(report.statusInfo.entryStatus)}
                          size="small" 
                          color={isOldMissingEntry ? 'error' : getStatusColor(report.statusInfo.entryStatus)}
                          sx={{ fontSize: '11px', height: 24, mb: 0.5 }}
                        />
                      )}
                      {report.attendanceMode !== 'dual' && report.statusInfo.exitStatus && report.anomaly !== 'missing_out' && (
                        <Chip 
                          label={localizeStatus(report.statusInfo.exitStatus)}
                          size="small" 
                          color={isOldMissingEntry ? 'error' : getStatusColor(report.statusInfo.exitStatus)}
                          sx={{ fontSize: '11px', height: 24, mb: 0.5 }}
                        />
                      )}
                      {/* Dual + anomali yok → durum hücresi boş kalmasın diye tire */}
                      {report.attendanceMode === 'dual' && !getAnomalyInfo(report) && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>—</Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {girisDeviceInfo && (
                        <Tooltip title={tr('Giriş Yapılan Cihaz Bilgileri - Detayları Görmek İçin Tıklayın', 'Check-in device information - click for details')}>
                          <Chip
                            icon={<SmartphoneIcon fontSize="small" style={{ color: '#1976d2' }} />}
                            label={`${tr('Giriş', 'In')}: ${girisDeviceModel.length > 12 ? girisDeviceModel.substring(0, 9) + '...' : girisDeviceModel}`}
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedDeviceInfo(girisDeviceInfo);
                              setDeviceActionType('giris');
                              setIsCihazModalOpen(true);
                            }}
                            sx={{
                              fontSize: '11px',
                              height: 22,
                              '& .MuiChip-icon': { fontSize: '0.9rem', mr: 0.3 },
                              '& .MuiChip-label': { px: 0.5 }
                            }}
                          />
                        </Tooltip>
                      )}
                      
                      {cikisDeviceInfo && (
                        <Tooltip title={tr('Çıkış Yapılan Cihaz Bilgileri - Detayları Görmek İçin Tıklayın', 'Check-out device information - click for details')}>
                          <Chip
                            icon={<SmartphoneIcon fontSize="small" style={{ color: '#9c27b0' }} />}
                            label={`${tr('Çıkış', 'Out')}: ${cikisDeviceModel.length > 12 ? cikisDeviceModel.substring(0, 9) + '...' : cikisDeviceModel}`}
                            variant="outlined"
                            size="small"
                            color="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedDeviceInfo(cikisDeviceInfo);
                              setDeviceActionType('cikis');
                              setIsCihazModalOpen(true);
                            }}
                            sx={{
                              fontSize: '11px',
                              height: 22,
                              '& .MuiChip-icon': { fontSize: '0.9rem', mr: 0.3 },
                              '& .MuiChip-label': { px: 0.5 }
                            }}
                          />
                        </Tooltip>
                      )}
                      
                      {!girisDeviceInfo && !cikisDeviceInfo && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px', display: 'flex', alignItems: 'center' }}>
                          <SmartphoneIcon fontSize="small" sx={{ fontSize: '0.9rem', mr: 0.5, opacity: 0.5 }} />
                          {tr('Cihaz Bilgisi Yok', 'No Device Info')}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {(girisKonum || cikisKonum) && (
                      <Tooltip title={tr('Vardiya Konumunu Görüntüle', 'View Shift Location')}>
                        <IconButton
                          color="secondary"
                          size="small"
                          onClick={(e) => {
                            handleOpenShiftLocationModal(report, e);
                          }}
                        >
                          <Navigation />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
                
                {/* Genel Toplam Satırı */}
                {filteredReports.length > 0 && (
                  <TableRow 
                    sx={{ 
                      backgroundColor: 'primary.light', 
                      '&:hover': { backgroundColor: 'primary.light' },
                      '& > td': { fontWeight: 'bold', color: 'white' }
                    }}
                  >
                    <TableCell colSpan={5} align="right">
                      <Typography variant="subtitle2" fontWeight="bold">
                        {tr('GENEL TOPLAM:', 'GRAND TOTAL:')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {calculateTotalWorkingTime(paginatedReports)}
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={3}></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
          
          {/* Desktop Sayfalama */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <TablePagination
              component="div"
              count={filteredReports.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={tr('Sayfa başına satır:', 'Rows per page:')}
              labelDisplayedRows={({ from, to, count }) =>
                tr(`${count} kayıttan ${from}-${to} arası gösteriliyor`, `Showing ${from}-${to} of ${count}`)
              }
            />
          </Box>
        </Box>
      )}
      
      {/* Vardiya Zaman Düzenleme Modalı */}
      <VardiyaZamanDuzenleModal
        open={isModalOpen}
        onClose={handleCloseModal}
        report={selectedReport}
      />
      
      {/* Cihaz Bilgileri Modalı */}
      <CihazBilgileriModal
        open={isCihazModalOpen}
        onClose={handleCloseCihazModal}
        deviceInfo={selectedDeviceInfo}
        actionType={deviceActionType}
      />

      {/* Vardiya Konum Modalı */}
      {selectedReportForLocation && (
        <ShiftLocationModal
          open={shiftLocationModalOpen}
          onClose={handleCloseShiftLocationModal}
          report={selectedReportForLocation}
        />
      )}
    </Box>
  );
};

export default GirisCikisRaporlari; 